import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Calendar, MapPin, Phone, Loader2, XCircle } from "lucide-react";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";

export default function BookingSuccess() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState("checking");
  const [booking, setBooking] = useState(null);
  const [salon, setSalon] = useState(null);

  useEffect(() => {
    api.get(`/public/salon/${slug}`).then((r) => setSalon(r.data)).catch(() => {});
    if (!sid) { setState("failed"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/public/payment/status/${sid}`);
        if (data.payment_status === "paid") { setBooking(data.booking); setState("paid"); return; }
        if (data.status === "expired" || data.payment_status === "failed") { setState("failed"); return; }
      } catch (e) { /* keep polling */ }
      if (tries++ < 10) setTimeout(poll, 2000); else setState("failed");
    };
    poll();
  }, [sid, slug]);

  const primary = salon?.primary_color || "#9A8C73";
  const secondary = salon?.secondary_color || "#2C3531";

  const calLink = () => {
    if (!booking) return "#";
    const d = booking.date.replace(/-/g, "");
    const start = `${d}T${(booking.slot_start || "09:00").replace(":", "")}00`;
    const end = `${d}T${(booking.slot_end || "10:00").replace(":", "")}00`;
    return `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(salon?.service_name || "Appointment")}&dates=${start}/${end}&location=${encodeURIComponent(salon?.address || "")}`;
  };

  return (
    <div className="min-h-screen grain flex items-center justify-center p-4" style={{ background: "#F4F1EC" }}>
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
        {state === "checking" && (
          <div className="p-12 text-center text-[#787672]"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />Confirming your payment…</div>
        )}
        {state === "failed" && (
          <div className="p-12 text-center" data-testid="booking-failed">
            <XCircle className="w-12 h-12 mx-auto mb-3 text-[#993333]" />
            <h2 className="font-serif text-2xl font-bold mb-2">Payment not completed</h2>
            <p className="text-sm text-[#787672] mb-6">Your booking wasn't confirmed. Please try again.</p>
            <Link to={`/book/${slug}`}><Button className="rounded-full" style={{ background: secondary }}>Back to booking</Button></Link>
          </div>
        )}
        {state === "paid" && booking && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="booking-success">
            <div className="p-8 text-center text-white" style={{ background: secondary }}>
              <div className="animate-pop w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: primary }}>
                <Check className="w-8 h-8 text-white" />
              </div>
              <h2 className="font-serif text-2xl font-bold">You're booked in!</h2>
              <p className="text-white/70 text-sm mt-1">Reference {booking.ref}</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="rounded-2xl border border-[#E8E6E1] divide-y divide-[#F0EFEB]">
                <Info icon={Calendar} text={`${booking.date} at ${booking.slot_start}`} />
                {salon?.address && <Info icon={MapPin} text={salon.address} />}
                {salon?.phone && <Info icon={Phone} text={salon.phone} />}
              </div>
              <div className="rounded-2xl p-4 text-sm" style={{ background: primary + "18" }}>
                <p className="font-semibold" style={{ color: secondary }}>Deposit paid £{booking.deposit_amount}</p>
                <p className="text-[#787672]">Balance £{booking.balance_due} due at your appointment.</p>
              </div>
              {salon?.prep_instructions && (
                <div className="rounded-2xl border border-[#E8E6E1] p-4">
                  <p className="uppercase tracking-[0.2em] text-[10px] font-bold text-[#787672] mb-1">Prep instructions</p>
                  <p className="text-sm">{salon.prep_instructions}</p>
                </div>
              )}
              {salon?.maps_embed && (
                <iframe title="map" src={salon.maps_embed} className="w-full h-40 rounded-2xl border border-[#E8E6E1]" loading="lazy" />
              )}
              <a href={calLink()} target="_blank" rel="noreferrer">
                <Button data-testid="add-calendar-btn" className="w-full rounded-full h-11 text-white font-semibold" style={{ background: secondary }}>
                  Add to Google Calendar
                </Button>
              </a>
              <p className="text-center text-xs text-[#787672]">A confirmation email & SMS have been sent.</p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

const Info = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-3 px-4 py-3">
    <Icon className="w-4 h-4 text-[#787672] shrink-0" /><span className="text-sm">{text}</span>
  </div>
);
